import React, { useEffect, useState } from "react";
import axios from "axios";
import Zoom from "react-medium-image-zoom";

import Modal from "./Modal";
import DocumentShow from "./DocumentShow";

import { ReactComponent as CheckEmpty } from "../../resources/icons/check_empty.svg";
import { ReactComponent as CheckFilled } from "../../resources/icons/check_filled.svg";
import { ReactComponent as Coins } from "../../resources/icons/coins_stacked.svg";
import { ReactComponent as Hourglass } from "../../resources/icons/hourglass.svg";
import { ReactComponent as DocumentIcon } from "../../resources/icons/document.svg";
import { ReactComponent as ImageIcon } from "../../resources/icons/image.svg";
import { getSubtaskDocumentPresignedUrl } from "../../api/projects";
import PDFPreviewModal from "./modals/PDFPreviewModal";

interface ProjectSubtaskSummary {
  id: number;
  name: string;
}

interface ProjectSubtaskDetail {
  id: number;
  name: string;
  isAdditional: boolean;
  time: number;
  budget: number;
  documentsCount: number;
  imagesCount: number;
  isCompleted: boolean;
  completedAt: string | null;
  documents: Array<{
    id: string;
    name: string;
    size: number;
    uploader: {
      id: number;
      name: string;
    };
    uploadedAt: string;
  }>;
  imageUrls: string[];
}

interface SubtaskListProps {
  subtasks: ProjectSubtaskSummary[];
  className?: string;
}

const SubtaskList: React.FC<SubtaskListProps> = ({ subtasks, className }) => {
  const [details, setDetails] = useState<ProjectSubtaskDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDocs, setSelectedDocs] = useState<
    ProjectSubtaskDetail["documents"]
  >([]);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [showDocsModal, setShowDocsModal] = useState(false);
  const [showImagesModal, setShowImagesModal] = useState(false);

  const [pdfModalUrl, setPdfModalUrl] = useState<string | null>(null);
  const [pdfModalTitle, setPdfModalTitle] = useState<string | null>(null);

  const [currentSubtaskName, setCurrentSubtaskName] = useState<string | null>(
    null
  );
  const [currentSubtaskNameForImages, setCurrentSubtaskNameForImages] =
    useState<string | null>(null);

  useEffect(() => {
    const fetchAllDetails = async () => {
      const token = localStorage.getItem("token");
      if (!token) return;
      try {
        setLoading(true);
        const promises = subtasks.map((st) =>
          axios
            .get<ProjectSubtaskDetail>(
              `${process.env.REACT_APP_URL}/project-subtasks/${st.id}`,
              { headers: { Authorization: `Bearer ${token}` } }
            )
            .then((res) => res.data)
        );
        const results = await Promise.all(promises);
        setDetails(results);
      } catch {
      } finally {
        setLoading(false);
      }
    };

    if (subtasks.length > 0) {
      fetchAllDetails();
    } else {
      setLoading(false);
    }
  }, [subtasks]);

  const openDocs = (subtaskId: number) => {
    const detail = details.find((d) => d.id === subtaskId);
    if (detail) {
      setSelectedDocs(detail.documents);
      setCurrentSubtaskName(detail.name);
      setShowDocsModal(true);
    }
  };

  const openImages = (subtaskId: number) => {
    const detail = details.find((d) => d.id === subtaskId);
    if (detail) {
      setSelectedImages(detail.imageUrls);
      setCurrentSubtaskNameForImages(detail.name);
      setShowImagesModal(true);
    }
  };

  const handlePreview = async (docId: string | number, docName: string) => {
    try {
      const { data, error } = await getSubtaskDocumentPresignedUrl(docId);

      if (error || !data?.file?.url) {
        return;
      }

      setShowDocsModal(false);
      setPdfModalUrl(data.file.url);
      setPdfModalTitle(decodeURIComponent(docName));
    } catch {}
  };

  if (loading) {
    return <div className={className}>Loading subtasks…</div>;
  }

  if (details.length === 0) {
    return <p className={`text-sm text-gray-500 ${className}`}>No subtasks</p>;
  }

  return (
    <>
      <div className={`grid grid-cols-2 gap-4 ${className || ""}`}>
        {details.map((d) => (
          <div key={d.id} className="bg-[#F9FAFB] rounded-lg p-4 flex flex-col">
            <div className="flex items-center ">
              {d.isCompleted ? (
                <CheckFilled className="w-5 h-5 text-[#475467] mr-4" />
              ) : (
                <CheckEmpty className="w-5 h-5 text-gray-400 mr-4" />
              )}
              <span
                className={`font-medium ${
                  d.isCompleted ? "inline-block line-through text-gray-500" : ""
                }`}
              >
                {d.name}
              </span>
            </div>

            <div className="mt-3 flex items-center space-x-4 text-sm text-gray-700">
              <div className="flex items-center space-x-1">
                <Coins className="w-4 h-4" />
                <span>{d.budget} BGN</span>
              </div>

              <div className="flex items-center space-x-1">
                <Hourglass className="w-4 h-4" />
                <span>{d.time}d</span>
              </div>

              <button
                onClick={() => openDocs(d.id)}
                className="flex items-center space-x-1"
              >
                <DocumentIcon className="w-4 h-4" />
                <span>{d.documentsCount}</span>
              </button>

              <button
                onClick={() => openImages(d.id)}
                className="flex items-center space-x-1"
              >
                <ImageIcon className="w-4 h-4" />
                <span>{d.imagesCount}</span>
              </button>
            </div>
          </div>
        ))}
      </div>

      <Modal isOpen={showDocsModal}>
        <div className="p-4 max-h-[70vh] overflow-y-auto">
          <h3 className="font-semibold mb-4">
            {currentSubtaskName
              ? `${currentSubtaskName} documents`
              : "Documents"}
          </h3>

          {selectedDocs.length === 0 ? (
            <p className="text-sm text-gray-500">No documents available.</p>
          ) : (
            <DocumentShow
              title=""
              docs={selectedDocs}
              onPreview={handlePreview}
              rowItems={{ xl: 2, lg: 2, xs: 1 }}
            />
          )}

          <div className="mt-4 text-right">
            <button
              onClick={() => setShowDocsModal(false)}
              className="px-4 py-2  text-buttonBlue rounded"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={showImagesModal}>
        <div className="p-4 max-h-[70vh] overflow-y-auto">
          <h3 className="font-semibold mb-4">
            {currentSubtaskNameForImages
              ? `${currentSubtaskNameForImages} images`
              : "Subtask images"}
          </h3>
          {selectedImages.length === 0 ? (
            <p className="text-sm text-gray-500">No images available.</p>
          ) : (
            <div className="grid grid-cols-3 gap-4">
              {selectedImages.map((url, idx) => (
                <Zoom key={idx}>
                  <img
                    src={url}
                    alt={`Subtask ${idx + 1}`}
                    className="w-full h-auto rounded-lg"
                  />
                </Zoom>
              ))}
            </div>
          )}
          <div className="mt-4 text-right">
            <button
              onClick={() => setShowImagesModal(false)}
              className="px-4 py-2  text-buttonBlue rounded"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>

      <PDFPreviewModal
        open={!!pdfModalUrl}
        url={pdfModalUrl!}
        name={pdfModalTitle!}
        onClose={() => {
          setPdfModalUrl(null);
          setPdfModalTitle(null);
        }}
      />
    </>
  );
};

export default SubtaskList;
